import { CONTRIBUTION_TYPES } from "Common/Constants";

import type { EmployeeProfile } from "Utils/types";

export const boundCosts = (premium: number, calculatedAmount: number) => Math.max(Math.min(premium, calculatedAmount), 0);

export const calculateCost = (premium: number, percent: number, fixed: number, contributionType: string, basePlanPremium: number) => {
  if (contributionType === CONTRIBUTION_TYPES.percentage) {
    return boundCosts(premium, premium * ((percent || 0) / 100));
  }
  if (contributionType === CONTRIBUTION_TYPES.percent_of_base_plan) {
    return boundCosts(premium, basePlanPremium * ((percent || 0) / 100));
  }
  return boundCosts(premium, fixed || 0);
};

export const getPremium = (employeesCosts: Array<any>, profileIndex: number, costType: string) => employeesCosts[profileIndex]?.[`${costType}_cost`];

export const dollarDisplay = (amount: string) => {
  if (amount === null) {
    return "---";
  }
  if (isNaN(amount?.toString().charAt(0))) {
    return "---";
  }
  return Number(amount).toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
  });
};

export const cleanEmployeeProfile = (employeeProfiles: EmployeeProfile) => {
  const cleanEmployeeProfile = [];
  employeeProfiles.forEach((employee, index) => {
    if (employee.age) {
      cleanEmployeeProfile.push({
        employee,
        index,
      });
    }
  });
  return cleanEmployeeProfile;
};

const COST_TYPE_SELF = "self";
const COST_TYPE_FAMILY = "family";
const COST_TYPES = [COST_TYPE_SELF, COST_TYPE_FAMILY];

export const tableRows = (
  contributionType: string,
  employeesCosts: Array<any>,
  employeeProfiles: EmployeeProfile,
  getContribution: (arg0: string) => any,
  medicalPlans: Array<any>,
): Array<any> => {
  const rows = [];

  cleanEmployeeProfile(employeeProfiles).map((employeeProfile) => {
    COST_TYPES.map((costType) => {
      const { employee } = employeeProfile;
      const employeeName = employee.name;
      const costIsSelf = costType === COST_TYPE_SELF;
      if (medicalPlans) {
        employeesCosts = medicalPlans[0].employee_costs;
        if (employee.selected_medical_plan) {
          const medicalPlan = medicalPlans.find((plan) => plan.id === employee.selected_medical_plan);

          if (medicalPlan?.employee_costs && medicalPlan.employee_costs[employeeProfile.index]) {
            employeesCosts = medicalPlan.employee_costs;
          }
        }
      }
      const premium = getPremium(employeesCosts, employeeProfile.index, costType);

      let basePlanPremium = getPremium(employeesCosts, employeeProfile.index, costType);

      if (medicalPlans) {
        basePlanPremium = getPremium(medicalPlans[0].employee_costs, employeeProfile.index, costType);
      }

      const contribution = getContribution(employeeName);
      if (costIsSelf || (costType === COST_TYPE_FAMILY && premium > 0.0)) {
        const row = {};
        const percent = costIsSelf ? contribution.employeePercentage : contribution.dependentPercentage;
        const fixed = costIsSelf ? contribution.employeeFixedAmount : contribution.dependentFixedAmount;

        const companyCost = calculateCost(premium, percent, fixed, contributionType, basePlanPremium);

        const employeeCost = premium - companyCost;

        row.dependent = !costIsSelf;
        row.name = costIsSelf ? employeeName : `${employeeName} Dependents`;
        row.companyCost = companyCost ? companyCost.toFixed(2) : null;
        row.companyCostInt = companyCost;
        row.employeeCost = employeeCost ? employeeCost.toFixed(2) : null;
        row.employeeCostInt = employeeCost;
        row.premium = premium ? premium.toFixed(2) : null;
        row.premiumInt = premium;
        row.rowType = costType;
        rows.push(row);
      }
    });
  });
  return rows;
};

export const calculateTotals = (rows: Array<any>) => {
  let totalCompanyCost = 0.0;
  let totalEmployeeCost = 0.0;
  let totalCost = 0.0;
  if (rows.length != 0) {
    rows.map((row) => {
      totalCompanyCost += row.companyCostInt;
      totalEmployeeCost += row.employeeCostInt;
      totalCost += row.premiumInt;
    });
  }
  return {
    totalCompanyCost,
    totalEmployeeCost,
    totalCost,
  };
};
